import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { ROLE_LABELS, type Role } from '@fleet/shared';
import { useSignOutMutation } from '@/api/endpoints';
import { useSession } from '@/app/session';
import { Button } from '@/design/Button';
import { Confirm } from '@/design/Confirm';

/**
 * The header carries two things and nothing else: whose business this is, and
 * who is signed in.
 *
 * New booking used to sit here too, on every screen at once. It went back to
 * the Bookings page, where the bookings are — a primary action repeated on
 * every screen stops reading as "the thing to do now" and starts reading as
 * furniture, and on a shared counter machine it put the app's most-used button
 * directly beside Sign out.
 */

interface HeaderProps {
  readonly onToggleNav: () => void;
  readonly navOpen: boolean;
}

export function Header({ onToggleNav, navOpen }: HeaderProps): ReactElement {
  // The business comes from the session, not from a file in the bundle: one
  // build now serves every tenant, and a bundled name would be the wrong
  // company's above the right company's bookings.
  const session = useSession();
  const [signOut, { isLoading: signingOut }] = useSignOutMutation();
  const [confirming, setConfirming] = useState(false);

  return (
    <header className="flex h-header items-center gap-3 border-b border-rule bg-panel px-3 sm:px-4">
      <button
        type="button"
        onClick={onToggleNav}
        aria-expanded={navOpen}
        aria-label={navOpen ? 'Hide sections' : 'Show sections'}
        className="-ml-1 grid h-9 w-9 place-items-center rounded text-ink-muted hover:bg-paper hover:text-ink lg:hidden"
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      </button>

      <div className="min-w-0">
        <p className="truncate font-semibold leading-tight">{session.business_name}</p>
        <p className="text-micro uppercase leading-tight text-ink-muted">
          {session.business_city}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {/* A link, because this is where somebody looks for "my account" —
            and the password screen had no way in from the header at all. */}
        <Link
          to="/account"
          className="hidden rounded px-1 text-right hover:bg-paper sm:block"
        >
          <p className="font-medium leading-tight">{session.name}</p>
          <p className="text-micro uppercase leading-tight text-ink-muted">
            {ROLE_LABELS[session.role as Role]}
          </p>
        </Link>

        <Button intent="quiet" onClick={() => setConfirming(true)} disabled={signingOut}>
          {signingOut ? 'Signing out' : 'Sign out'}
        </Button>
      </div>

      {/*
        Asked, because at a shared counter this button is one slip away from
        the New booking button beside it — and signing out mid-booking loses
        whatever was half typed, with a customer still on the phone.
      */}
      <Confirm
        open={confirming}
        title="Sign out?"
        detail={
          <>
            Anything half-filled on screen will be lost, and the next person will have to sign
            in before they can take a booking.
          </>
        }
        confirmLabel="Sign out"
        busy={signingOut}
        onConfirm={() => {
          setConfirming(false);
          void signOut();
        }}
        onCancel={() => setConfirming(false)}
      />
    </header>
  );
}
